import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "./ui/button";

const PHONE_NUMBER = "+41 79 852 10 14";
const WHATSAPP_LINK = "https://wa.me/41798521014";

const navLinks = [
  { href: "/", label: "Accueil" },
  { href: "/comment-ca-marche", label: "Comment ça marche" },
  { href: "/a-propos", label: "À propos" },
  { href: "/faq", label: "FAQ" },
  { href: "/contact", label: "Contact" },
  { href: "/estimation", label: "Estimation" },
];

export default function Layout({ children }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header
        data-testid="header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-md py-2 md:py-3"
            : "bg-white py-3 md:py-4"
        }`}
      >
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              data-testid="logo-link"
              className="flex items-center"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_quick-car-sale-2/artifacts/gumvxtyh_logo.png" 
                alt="Achat Auto Occasion"
                className="h-10 md:h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.href.replace("/", "") || "home"}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    location.pathname === link.href
                      ? "bg-primary text-white"
                      : "text-neutral-text hover:bg-primary/5 hover:text-primary"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Phone - Desktop */}
              <a
                href={`tel:${PHONE_NUMBER.replace(/\s/g, "")}`}
                data-testid="header-phone"
                className="hidden md:flex items-center gap-2 text-primary hover:text-accent transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm font-semibold">{PHONE_NUMBER}</span>
              </a>

              {/* CTA Button */}
              <Link to="/estimation" data-testid="header-cta">
                <Button className="bg-accent hover:bg-accent-hover text-white font-bold rounded-full px-6 shadow-lg hover:shadow-accent/30 transition-all duration-300 hidden sm:flex">
                  Estimation gratuite
                </Button>
              </Link>

              {/* Mobile menu button */}
              <button
                data-testid="mobile-menu-btn"
                className="lg:hidden p-2 text-primary hover:bg-primary/5 rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <>
                {/* Backdrop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                  onClick={() => setMobileMenuOpen(false)}
                />
                {/* Menu Panel */}
                <motion.nav
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ type: "spring", damping: 25, stiffness: 200 }}
                  data-testid="mobile-nav"
                  className="fixed top-0 right-0 h-full w-[85%] max-w-sm bg-white z-50 lg:hidden shadow-2xl"
                >
                  <div className="p-6">
                    {/* Close button */}
                    <div className="flex justify-end mb-8">
                      <button
                        onClick={() => setMobileMenuOpen(false)}
                        className="p-2 text-neutral-text hover:bg-neutral-background rounded-lg"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      {navLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          data-testid={`mobile-nav-${link.href.replace("/", "") || "home"}`}
                          className={`px-4 py-4 rounded-xl font-medium text-lg transition-all ${
                            location.pathname === link.href
                              ? "bg-primary text-white"
                              : "text-neutral-text hover:bg-primary/5"
                          }`}
                        >
                          {link.label}
                        </Link>
                      ))}
                      <Link to="/estimation" className="mt-4">
                        <Button
                          data-testid="mobile-cta"
                          className="w-full bg-accent hover:bg-accent-hover text-white font-bold rounded-full h-14 text-lg"
                        >
                          Estimation gratuite
                        </Button>
                      </Link>
                      <a
                        href={`tel:${PHONE_NUMBER.replace(/\s/g, "")}`}
                        className="flex items-center justify-center gap-2 py-4 text-primary font-semibold mt-2"
                      >
                        <Phone className="w-5 h-5" />
                        {PHONE_NUMBER}
                      </a>
                    </div>
                  </div>
                </motion.nav>
              </>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 pt-16 md:pt-20">{children}</main>

      {/* Footer */}
      <footer data-testid="footer" className="bg-primary text-white pb-24 md:pb-0">
        <div className="container mx-auto px-4 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Brand */}
            <div>
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_quick-car-sale-2/artifacts/gumvxtyh_logo.png" 
                  alt="Achat Auto Occasion"
                  className="h-14 w-auto brightness-0 invert"
                />
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                Spécialiste du rachat de véhicules d'occasion en Suisse romande.
                Paiement cash, déplacement gratuit, service rapide et professionnel.
              </p>
            </div>

            {/* Quick links */}
            <div>
              <h4 className="font-chivo font-bold text-lg mb-4">Navigation</h4>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-white/70 hover:text-accent transition-colors text-sm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/estimation"
                    className="text-accent hover:text-white transition-colors text-sm font-semibold"
                  >
                    Demander une estimation
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-chivo font-bold text-lg mb-4">Contact</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <Phone className="w-4 h-4 mt-0.5 text-accent" />
                  <div>
                    <a
                      href={`tel:${PHONE_NUMBER.replace(/\s/g, "")}`}
                      className="text-white hover:text-accent transition-colors block"
                    >
                      {PHONE_NUMBER}
                    </a>
                    <span className="text-white/50 text-xs">7j/7 de 07h à 22h</span>
                  </div>
                </li>
                <li className="text-white/70">
                  <span className="text-white">Email:</span>{" "}
                  <a
                    href="mailto:info@achat-auto-occasion.ch"
                    className="hover:text-accent transition-colors"
                  >
                    info@achat-auto-occasion.ch
                  </a>
                </li>
                <li>
                  <a
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white px-4 py-2 rounded-full text-sm font-semibold transition-colors"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    WhatsApp
                  </a>
                </li>
              </ul>
            </div>

            {/* Coverage */}
            <div>
              <h4 className="font-chivo font-bold text-lg mb-4">Zone de couverture</h4>
              <p className="text-white/70 text-sm mb-3">
                Nous intervenons dans toute la Suisse romande :
              </p>
              <ul className="grid grid-cols-2 gap-1 text-sm text-white/70">
                <li>• Vaud</li>
                <li>• Genève</li>
                <li>• Valais</li>
                <li>• Fribourg</li>
                <li>• Neuchâtel</li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/50">
            <p>© 2026 Achat Auto Occasion — Tous droits réservés</p>
            <p>
              Site réalisé par{" "}
              <a
                href="https://lynk.marketing"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-white transition-colors"
              >
                Lynk — Agence digitale 360°
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform whatsapp-pulse"
        aria-label="Nous contacter sur WhatsApp"
      >
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>
    </div>
  );
}
